'use client';

import { useEffect, useState } from 'react';

import { ProPaymentPlans } from '../../../components/ProPaymentPlans/ProPaymentPlans';
import { Redirect } from '../../../components/Redirect/Redirect';
import { StickyBannerGetPro } from '../../../components/StickyBottomBanner/StickyBannerGetPro';
import { PRIMARY_OFFER_ID, type ProPlanId } from '@domain/premium/plans';
import { useAnalytics } from '@domain/analytics/AnalyticsContext';
import { trackEvent } from '@domain/analytics/trackEvent';
import { useCoupon } from '@domain/premium/useCoupon';
import { useCurrentURL } from '@hooks/useCurrentURL';
import { useDistanceFromTop } from '@hooks/useDistanceFromTop';
import { useIsMobileDevice } from '@hooks/useIsMobileDevice';
import { useScreenHeight } from '@hooks/useScreenHeight';
import { useClientHasRendered } from '@domain/session/useClientHasRendered';
import { useUserInfo } from '@domain/user/useUserInfo';
import {
  companiesURLParameters,
  flowsURLParameters,
  URLParameters,
} from '../../Onboarding/constants/url';

export const ProPaymentPlansSection = ({ couponCode }: { couponCode?: string }) => {
  const { isPremium, premiumEndDate } = useUserInfo();
  const { coupon, isLoading: couponIsLoading } = useCoupon(couponCode);
  const [proPlanId, setProPlanId] = useState<ProPlanId | undefined>();
  const clientHasRendered = useClientHasRendered();
  const userHasBeenPro =
    clientHasRendered &&
    !!premiumEndDate &&
    premiumEndDate.localeCompare(new Date().toString()) <= 0;

  const isMobileDevice = useIsMobileDevice();
  const distanceFromTop = useDistanceFromTop();
  const screenHeight = useScreenHeight();

  const { currentPage } = useAnalytics();
  const { addParameters, hasParameters } = useCurrentURL();
  const submitLink = addParameters({
    [URLParameters.Company]: companiesURLParameters.fishbrain,
    [URLParameters.Flow]: flowsURLParameters.fishbrain.becomePro,
  });
  const hasOnboardingUrlParams = hasParameters([URLParameters.Company, URLParameters.Flow]);

  const handleSubmitLinkClick = (): void => {
    sessionStorage.setItem('subscription.plan.id', JSON.stringify(proPlanId));
    trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        type: 'pro',
        source: 'pro-payment-plan',
        page: currentPage,
      },
    });
  };

  useEffect(() => {
    const handleSessionStorageChange = (): void => {
      const planIdFromStorage = sessionStorage.getItem('subscription.plan.id');
      const planFromStorage = planIdFromStorage ? JSON.parse(planIdFromStorage) : PRIMARY_OFFER_ID;
      setProPlanId(planFromStorage);
    };

    window.addEventListener('planInStorageChanged', handleSessionStorageChange);

    handleSessionStorageChange();

    return () => {
      window.removeEventListener('planInStorageChanged', handleSessionStorageChange);
    };
  }, []);

  useEffect(() => {
    if (coupon) {
      setProPlanId(coupon.group.stripe_plan.external_id);
    }
  }, [coupon]);

  if (isPremium && !hasOnboardingUrlParams) {
    return <Redirect to="/pro/overview" />;
  }

  return (
    <>
      <ProPaymentPlans
        className="col-start-1 row-start-2 px-6"
        coupon={coupon}
        couponIsLoading={couponIsLoading}
        backgroundColour="bg-sea-foam"
        submitLink={submitLink}
        onSubmitLinkClick={handleSubmitLinkClick}
        proPlanId={proPlanId}
        onPlanSelect={setProPlanId}
        userHasBeenPro={userHasBeenPro}
      />{' '}
      {isMobileDevice && distanceFromTop > (screenHeight / 3) * 2 && (
        <StickyBannerGetPro
          selectedPlanId={proPlanId}
          coupon={coupon}
          couponIsLoading={couponIsLoading}
          submitLink={submitLink}
        />
      )}
    </>
  );
};
